import { useState } from "react";
import ReactGA from "react-ga4";
import { AnimatePresence, motion } from "framer-motion";

import { CloseIcon } from "@chakra-ui/icons";
import { Box, Flex, IconButton, Modal, ModalContent, ModalOverlay, Text, Textarea, Button, useColorModeValue, useToast } from "@chakra-ui/react";
import NetlifyHoneypot from "./NetlifyHoneypot";
import NetlifyRecaptcha, { isRecaptchaEnabled } from "./NetlifyRecaptcha";

const FeedbackModal = ({ isOpen, onClose }) => {
  const toast = useToast();
  const [feedback, setFeedback] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");

  const bg      = useColorModeValue("#fdfaf5", "#1e1510");
  const border  = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.07)");
  const text    = useColorModeValue("#1a0c04", "#e8d9c4");
  const dim     = useColorModeValue("#6a4020", "#a89880");
  const countBg = useColorModeValue("rgba(0,0,0,0.04)", "rgba(255,255,255,0.04)");

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setFeedback("");
      setSubmitted(false);
      setCaptchaToken("");
      setSending(false);
    }, 300);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isRecaptchaEnabled && !captchaToken) {
      toast({
        title: "Complete the captcha first.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setSending(true);
    const body = new URLSearchParams(new FormData(e.target));
    if (captchaToken) body.set("g-recaptcha-response", captchaToken);

    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`${res.status}`);
        setSubmitted(true);
        ReactGA.event({ category: "Form", action: "Submit Success", label: "Feedback Form" });
        setTimeout(() => { handleClose(); }, 2200);
      })
      .catch(() => {
        toast({
          title: "Couldn't send feedback.",
          description: "Something went wrong. Try again in a moment.",
          status: "error",
          duration: 6000,
          isClosable: true,
        });
      })
      .finally(() => setSending(false));
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered>
      <ModalOverlay backdropFilter="blur(6px)" bg="blackAlpha.300" />
      <ModalContent
        bg={bg}
        border="1px solid"
        borderColor={border}
        borderRadius="20px"
        boxShadow="0 24px 64px rgba(0,0,0,0.18)"
        mx={4}
        p={0}
        overflow="hidden"
      >
        <AnimatePresence mode="wait">
          {submitted ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            >
              <Flex direction="column" align="center" justify="center" py={10} px={6} textAlign="center">
                <Text fontSize="36px" mb={4} lineHeight={1}>☕</Text>
                <Text
                  fontFamily="heading"
                  fontSize="22px" fontWeight={400} color={text} mb={2}
                >
                  Thanks.
                </Text>
                <Text fontSize="13px" color={dim} lineHeight={1.7}>
                  We appreciate every bit of it.
                </Text>
              </Flex>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <Flex align="center" justify="space-between" px={6} pt={5} pb={3}>
                <Text
                  fontFamily="heading"
                  fontSize="18px" fontWeight={400} color={text}
                >
                  What's on your mind?
                </Text>
                <IconButton
                  icon={<CloseIcon boxSize="10px" />}
                  variant="ghost"
                  size="sm"
                  onClick={handleClose}
                  aria-label="Close"
                />
              </Flex>

              <Box
                as="form"
                position="relative"
                px={6} pb={6}
                onSubmit={handleSubmit}
                name="feedback"
                method="POST"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
              >
                <input type="hidden" name="form-name" value="feedback" />
                <NetlifyHoneypot />

                <Box position="relative">
                  <Textarea
                    name="feedbackMessage"
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    placeholder="All feedback is good feedback"
                    rows={4}
                    resize="none"
                    maxLength={500}
                    fontFamily="body"
                    fontSize="14px"
                    pb={feedback.length > 0 ? "32px" : undefined}
                  />
                  {feedback.length > 0 && (
                    <Flex
                      position="absolute" bottom="10px" right="12px"
                      bg={countBg} px={2} py="2px" borderRadius="full"
                      pointerEvents="none"
                    >
                      <Text fontSize="11px" color={dim} fontFamily="body">
                        {feedback.length} / 500
                      </Text>
                    </Flex>
                  )}
                </Box>

                <Box mt={3}>
                  <NetlifyRecaptcha onChange={setCaptchaToken} />
                </Box>

                <Button
                  mt={3} type="submit" w="full"
                  isLoading={sending}
                  isDisabled={!feedback.trim() || (isRecaptchaEnabled && !captchaToken)}
                  opacity={feedback.trim() && (!isRecaptchaEnabled || captchaToken) ? 1 : 0.45}
                  transition="opacity 0.15s ease"
                >
                  Send feedback
                </Button>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </ModalContent>
    </Modal>
  );
};

export default FeedbackModal;
